import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getFormationCoords } from '../utils/gameData';

/* =========================================================================
 * Modern broadcast-style football pitch.
 * - Smooth player wandering with cubic-bezier easing
 * - Ball with motion trail that follows real attacking direction
 * - Goal celebration: radial burst + screen flash + scoreline pop
 * - Broadcast scoreline chip with team initials + live pulse
 * - Kit-colored player chips derived from team name hash
 * ========================================================================= */

// Hash a string to a stable HSL color (used as team kit accent)
const teamColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return { hue, primary: `hsl(${hue}, 78%, 52%)`, dark: `hsl(${hue}, 75%, 32%)`, light: `hsl(${hue}, 90%, 70%)` };
};

const initials = (name = '') => name
  .split(' ')
  .filter(w => w && w[0] && /[A-Za-z]/.test(w[0]))
  .map(w => w[0])
  .slice(0, 3)
  .join('')
  .toUpperCase() || '–';

// Surname only, capped to 9 chars for the on-pitch name plate so it fits
// inside the broadcast box without breaking the line layout.
const surname = (full = '') => {
  const tail = full.trim().split(/\s+/).slice(-1)[0] || full;
  return tail.length > 9 ? tail.slice(0, 9).toUpperCase() : tail.toUpperCase();
};

export const MatchPitchGraphics = ({
  homeTeam, awayTeam, events = [], currentMinute = 0, isLive = false,
  homeScore = 0, awayScore = 0, matchRatings = {},
}) => {
  const [ballPosition, setBallPosition] = useState({ x: 50, y: 50 });
  const [ballTrail, setBallTrail] = useState([]);
  const [lastEventCount, setLastEventCount] = useState(0);
  const [animatingGoal, setAnimatingGoal] = useState(null);
  const [showRatings, setShowRatings] = useState(false);
  const [playerJitter, setPlayerJitter] = useState({});
  const [scorePulse, setScorePulse] = useState(null); // 'home' | 'away'
  const ballAnchorRef = useRef({ x: 50, y: 50, target: { x: 50, y: 50 } });

  const homeKit = useMemo(() => teamColor(homeTeam?.name || 'Home'), [homeTeam?.name]);
  const awayKit = useMemo(() => teamColor((awayTeam?.name || 'Away') + 'X'), [awayTeam?.name]);

  // Attack direction (home attacks up, away attacks down)
  const homeCoords = homeTeam ? getFormationCoords(homeTeam.formation) : [];
  const awayCoords = awayTeam ? getFormationCoords(awayTeam.formation) : [];

  // Smooth ball wandering — pick a target, ease toward it
  useEffect(() => {
    if (!isLive || animatingGoal) return;
    let raf;
    const tick = () => {
      const a = ballAnchorRef.current;
      const dx = a.target.x - a.x;
      const dy = a.target.y - a.y;
      a.x += dx * 0.06;
      a.y += dy * 0.06;
      setBallPosition({ x: a.x, y: a.y });
      setBallTrail(prev => {
        const next = [...prev.slice(-9), { x: a.x, y: a.y, t: Date.now() }];
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    const targetTick = setInterval(() => {
      // bias slightly toward direction of last "shot" or toward center, but mostly random
      ballAnchorRef.current.target = {
        x: 14 + Math.random() * 72,
        y: 14 + Math.random() * 72,
      };
    }, 1200);
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); clearInterval(targetTick); };
  }, [isLive, animatingGoal]);

  // Player wandering jitter
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => {
      const next = {};
      const homeLen = homeCoords.length || 11;
      const awayLen = awayCoords.length || 11;
      const bx = ballPosition.x, by = ballPosition.y;
      for (let i = 0; i < homeLen; i++) {
        const base = homeBase(i);
        const pull = pullToBall(base, bx, by);
        next[`h-${i}`] = {
          dx: (Math.random() * 5 - 2.5) + pull.dx,
          dy: (Math.random() * 5 - 2.5) + pull.dy,
        };
      }
      for (let i = 0; i < awayLen; i++) {
        const base = awayBase(i);
        const pull = pullToBall(base, bx, by);
        next[`a-${i}`] = {
          dx: (Math.random() * 5 - 2.5) + pull.dx,
          dy: (Math.random() * 5 - 2.5) + pull.dy,
        };
      }
      setPlayerJitter(next);
    }, 850);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [isLive, ballPosition.x, ballPosition.y, homeTeam?.formation, awayTeam?.formation]);

  // React to new events (goals, saves, kickoff)
  useEffect(() => {
    if (events.length > lastEventCount) {
      const latest = events[events.length - 1];
      setLastEventCount(events.length);
      if (latest.type === 'goal') {
        const isHome = latest.team === 'home';
        const targetY = isHome ? 5 : 95;
        ballAnchorRef.current.target = { x: 50, y: targetY };
        setAnimatingGoal(latest.team);
        setScorePulse(latest.team);
        setTimeout(() => setScorePulse(null), 1100);
        setTimeout(() => {
          setAnimatingGoal(null);
          ballAnchorRef.current.target = { x: 50, y: 50 };
          ballAnchorRef.current.x = 50; ballAnchorRef.current.y = 50;
          setBallTrail([]);
        }, 1800);
      } else if (latest.type === 'save') {
        const isHome = latest.team === 'home';
        ballAnchorRef.current.target = { x: 50 + (Math.random() * 14 - 7), y: isHome ? 12 : 88 };
      } else if (['kickoff', 'halftime', 'fulltime'].includes(latest.type)) {
        ballAnchorRef.current.target = { x: 50, y: 50 };
      }
    }
  }, [events, lastEventCount]);

  if (!homeTeam || !awayTeam) return null;

  const getPlayerByIndex = (team, index) => {
    const num = team.lineup?.[index];
    return team.players?.find(p => p.number === num);
  };

  function homeBase(idx) {
    const c = homeCoords[idx] || { x: 50, y: 50 };
    // Home team occupies bottom half (50-97). y in coords is 0=top, 100=bottom of attacking half.
    return { x: c.x, y: c.y * 0.44 + 51 };
  }
  function awayBase(idx) {
    const c = awayCoords[idx] || { x: 50, y: 50 };
    return { x: c.x, y: (100 - c.y) * 0.44 + 5 };
  }
  function pullToBall(base, bx, by) {
    const dx = bx - base.x, dy = by - base.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 18) return { dx: dx * 0.18, dy: dy * 0.18 };
    return { dx: 0, dy: 0 };
  }

  const ratingColor = (r) => {
    if (r >= 9.0) return '#FFD700'; // great / perfect — gold
    if (r >= 8.0) return '#32D74B'; // actual good — emerald
    if (r >= 7.0) return '#4DA3FF'; // normal good — blue
    if (r >= 6.0) return '#A0A0A5'; // average — grey
    return '#FF3B30';               // below 6 — red
  };

  const homeInit = initials(homeTeam.name);
  const awayInit = initials(awayTeam.name);

  return (
    <div className="ewfl-pitch-broadcast" data-testid="match-pitch-graphics">
      {/* ===== Pitch base ===== */}
      <div className="ewfl-pitch-grass">
        {/* mowing stripes */}
        {[...Array(14)].map((_, i) => (
          <div
            key={i}
            className="ewfl-pitch-stripe"
            style={{
              top: `${i * 7.14}%`,
              background: i % 2 === 0
                ? 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))'
                : 'linear-gradient(180deg, rgba(0,0,0,0.18), rgba(0,0,0,0.06))',
            }}
          />
        ))}
        <div className="ewfl-pitch-vignette" />
        <div className="ewfl-pitch-noise" />
      </div>

      {/* ===== Pitch markings ===== */}
      <svg className="ewfl-pitch-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <filter id="line-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="0.2" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <g stroke="rgba(255,255,255,0.7)" strokeWidth="0.35" fill="none" filter="url(#line-glow)">
          <rect x="3" y="3" width="94" height="94" />
          <line x1="3" y1="50" x2="97" y2="50" />
          <circle cx="50" cy="50" r="10" />
          <circle cx="50" cy="50" r="0.9" fill="rgba(255,255,255,0.85)" />
          <rect x="22" y="3" width="56" height="17" />
          <rect x="32" y="3" width="36" height="7" />
          <rect x="22" y="80" width="56" height="17" />
          <rect x="32" y="90" width="36" height="7" />
          <circle cx="50" cy="13" r="0.7" fill="rgba(255,255,255,0.85)" />
          <circle cx="50" cy="87" r="0.7" fill="rgba(255,255,255,0.85)" />
          <path d="M 38 20 Q 50 26 62 20" />
          <path d="M 38 80 Q 50 74 62 80" />
          <path d="M 3 5 Q 5 3 7 3" />
          <path d="M 93 3 Q 97 3 97 7" />
          <path d="M 3 93 Q 3 97 7 97" />
          <path d="M 93 97 Q 97 97 97 93" />
        </g>
        {/* Goals */}
        <rect x="42" y="0.5" width="16" height="2.5" fill={awayKit.primary} opacity="0.4" stroke={awayKit.light} strokeWidth="0.4" />
        <rect x="42" y="97" width="16" height="2.5" fill={homeKit.primary} opacity="0.4" stroke={homeKit.light} strokeWidth="0.4" />
      </svg>

      {/* ===== Goal flash ===== */}
      {animatingGoal && (
        <>
          <div
            className="ewfl-goal-flash"
            style={{
              background: `radial-gradient(circle at 50% ${animatingGoal === 'home' ? '95%' : '5%'}, ${(animatingGoal === 'home' ? homeKit.light : awayKit.light)}55 0%, transparent 60%)`,
            }}
          />
          <div className={`ewfl-goal-rays ewfl-goal-rays--${animatingGoal}`}>
            {[...Array(12)].map((_, i) => (
              <span key={i} style={{ transform: `rotate(${i * 30}deg)`, background: animatingGoal === 'home' ? homeKit.primary : awayKit.primary }} />
            ))}
          </div>
          <div className="ewfl-goal-banner" style={{ background: `linear-gradient(135deg, ${(animatingGoal === 'home' ? homeKit.primary : awayKit.primary)}, ${(animatingGoal === 'home' ? homeKit.dark : awayKit.dark)})` }}>
            <span className="ewfl-goal-banner-text">GOAL</span>
            <span className="ewfl-goal-banner-team">{animatingGoal === 'home' ? homeTeam.name : awayTeam.name}</span>
          </div>
        </>
      )}

      {/* ===== Away players (top half) ===== */}
      {awayCoords.map((coord, idx) => {
        const player = getPlayerByIndex(awayTeam, coord.playerIndex);
        if (!player) return null;
        const rating = matchRatings[player.id];
        const j = playerJitter[`a-${idx}`] || { dx: 0, dy: 0 };
        const baseX = coord.x, baseY = (100 - coord.y) * 0.44 + 5;
        const x = Math.max(3, Math.min(97, baseX + j.dx));
        const y = Math.max(3, Math.min(50, baseY + j.dy));
        return (
          <div key={`away-${player.id}`} className="ewfl-player ewfl-player--away"
            style={{ left: `${x}%`, top: `${y}%` }}>
            <div
              className="ewfl-player-chip"
              style={{
                background: `linear-gradient(180deg, ${awayKit.primary}, ${awayKit.dark})`,
                boxShadow: `0 0 0 2px ${awayKit.light}, 0 8px 18px ${awayKit.dark}88`,
              }}
            >
              {player.number}
            </div>
            {showRatings && (
              <div className="ewfl-pp" data-testid={`player-plate-away-${player.number}`}>
                <span className="ewfl-pp-name">{surname(player.name)}</span>
                <span className="ewfl-pp-sep">·</span>
                <span className="ewfl-pp-pos">{player.position}</span>
                <span className="ewfl-pp-sep">·</span>
                <span className="ewfl-pp-ovr">{player.overall}</span>
                {rating != null && (
                  <span
                    className="ewfl-pp-rating"
                    style={{
                      color: ratingColor(rating),
                      borderColor: ratingColor(rating) + '66',
                      background: ratingColor(rating) + '22',
                    }}
                  >
                    {Number(rating).toFixed(1)}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ===== Home players (bottom half) ===== */}
      {homeCoords.map((coord, idx) => {
        const player = getPlayerByIndex(homeTeam, coord.playerIndex);
        if (!player) return null;
        const rating = matchRatings[player.id];
        const j = playerJitter[`h-${idx}`] || { dx: 0, dy: 0 };
        const baseX = coord.x, baseY = coord.y * 0.44 + 51;
        const x = Math.max(3, Math.min(97, baseX + j.dx));
        const y = Math.max(50, Math.min(97, baseY + j.dy));
        return (
          <div key={`home-${player.id}`} className="ewfl-player ewfl-player--home"
            style={{ left: `${x}%`, top: `${y}%` }}>
            <div
              className="ewfl-player-chip"
              style={{
                background: `linear-gradient(180deg, ${homeKit.primary}, ${homeKit.dark})`,
                boxShadow: `0 0 0 2px ${homeKit.light}, 0 8px 18px ${homeKit.dark}88`,
              }}
            >
              {player.number}
            </div>
            {showRatings && (
              <div className="ewfl-pp" data-testid={`player-plate-home-${player.number}`}>
                <span className="ewfl-pp-name">{surname(player.name)}</span>
                <span className="ewfl-pp-sep">·</span>
                <span className="ewfl-pp-pos">{player.position}</span>
                <span className="ewfl-pp-sep">·</span>
                <span className="ewfl-pp-ovr">{player.overall}</span>
                {rating != null && (
                  <span
                    className="ewfl-pp-rating"
                    style={{
                      color: ratingColor(rating),
                      borderColor: ratingColor(rating) + '66',
                      background: ratingColor(rating) + '22',
                    }}
                  >
                    {Number(rating).toFixed(1)}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ===== Ball trail ===== */}
      {ballTrail.map((p, i) => {
        const opacity = (i + 1) / ballTrail.length * 0.55;
        const size = 4 + (i / ballTrail.length) * 6;
        return (
          <div key={i}
            className="ewfl-ball-trail"
            style={{
              left: `${p.x}%`, top: `${p.y}%`, opacity,
              width: `${size}px`, height: `${size}px`
            }}
          />
        );
      })}

      {/* ===== Ball ===== */}
      <div
        className={`ewfl-ball ${animatingGoal ? 'ewfl-ball--goal' : ''}`}
        style={{ left: `${ballPosition.x}%`, top: `${ballPosition.y}%` }}
      >
        <div className="ewfl-ball-shape" />
      </div>

      {/* ===== Broadcast scoreline ===== */}
      <div className="ewfl-scoreline">
        <div className="ewfl-scoreline-side" style={{ borderColor: homeKit.primary }}>
          <span className="ewfl-scoreline-badge" style={{ background: `linear-gradient(135deg, ${homeKit.primary}, ${homeKit.dark})` }}>{homeInit}</span>
          <span className="ewfl-scoreline-team">{homeTeam.name}</span>
        </div>
        <div className={`ewfl-scoreline-score ${scorePulse === 'home' ? 'ewfl-pulse-home' : ''}`}>{homeScore}</div>
        <div className="ewfl-scoreline-clock">
          <span className="ewfl-scoreline-clock-text">{currentMinute}<span className="ewfl-tick">'</span></span>
          {isLive && <span className="ewfl-live-dot" />}
          {!isLive && currentMinute >= 90 && <span className="ewfl-ft">FT</span>}
        </div>
        <div className={`ewfl-scoreline-score ${scorePulse === 'away' ? 'ewfl-pulse-away' : ''}`}>{awayScore}</div>
        <div className="ewfl-scoreline-side ewfl-scoreline-side--right" style={{ borderColor: awayKit.primary }}>
          <span className="ewfl-scoreline-team">{awayTeam.name}</span>
          <span className="ewfl-scoreline-badge" style={{ background: `linear-gradient(135deg, ${awayKit.primary}, ${awayKit.dark})` }}>{awayInit}</span>
        </div>
      </div>

      {/* ===== Bottom info row ===== */}
      <div className="ewfl-pitch-bottom">
        <span className="ewfl-form-chip ewfl-form-chip--home" style={{ borderColor: homeKit.primary }}>
          <span className="ewfl-form-dot" style={{ background: homeKit.primary }} /> {homeTeam.formation}
        </span>
        <button
          onClick={() => setShowRatings(p => !p)}
          className="ewfl-ratings-toggle"
          data-testid="toggle-ratings-btn"
        >
          {showRatings ? 'Hide ratings' : 'Show ratings'}
        </button>
        <span className="ewfl-form-chip ewfl-form-chip--away" style={{ borderColor: awayKit.primary }}>
          {awayTeam.formation} <span className="ewfl-form-dot" style={{ background: awayKit.primary }} />
        </span>
      </div>
    </div>
  );
};
